import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button } from '../ui';
import { UserIcon, CalendarDaysIcon, DocumentTextIcon, PlusIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import DashboardFirstRun from './DashboardFirstRun';
import DropdownMenu from '../ui/DropdownMenu';
import PageHeader from '../ui/PageHeader';
import { invoiceAPI, quoteAPI, jobAPI, customerAPI, unwrapList } from '../../services/api';
import PeriodSelector from '../ui/PeriodSelector';
import KPIRow from './KPIRow';
import QuotesPipelineDonut from './QuotesPipelineDonut';
import ScheduleStrip from './ScheduleStrip';
import ActivityFeed from './ActivityFeed';
import DashboardJobsTable from './DashboardJobsTable';
import { computeKPIs, computeQuotesPipeline, buildActivityFeed } from './dashboardData';
import { Cog6ToothIcon } from '@heroicons/react/20/solid';
import ChartErrorBoundary from '../ui/ChartErrorBoundary';
import { useOptionalAuth } from '../../contexts/AuthContext';
import { isSetupAdmin } from '../../utils/isSetupAdmin';

// Enrich records with customer names from the customer list
function enrichWithCustomerNames(records, customerMap) {
  return records.map(record => {
    if (record.customerName || record.customer_name) return record;
    const custId = record.customerId || record.customer_id;
    if (custId && customerMap[custId]) {
      return { ...record, customerName: customerMap[custId] };
    }
    return record;
  });
}

const STORAGE_KEY = 'bidit_dashboard_prefs';

function loadPrefs() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) { /* ignore */ }
  return null;
}

function savePrefs(prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch (e) { /* ignore */ }
}

// IDI-149: gear-menu settings-list order mirrors on-page tile render order.
// On-page tile order was set in iter-18 (commit 919e2f8, "per operator Round 4"):
// KPIs → Charts+Schedule → Jobs Table → Recent Activity. This array must stay in
// sync with the isVisible(...) call sites below. Do NOT rename any `id` — the
// ids are persisted in the `hiddenSections` localStorage array (STORAGE_KEY).
const SECTION_DEFS = [
  { id: 'kpis', label: 'KPI Cards' },
  { id: 'charts', label: 'Charts' },
  { id: 'schedule', label: "This Week's Schedule" },
  { id: 'jobs_table', label: 'Jobs Table' },
  { id: 'activity', label: 'Recent Activity' },
];

export default function DashboardNew() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const authUser = useOptionalAuth()?.user;
  const savedPrefs = loadPrefs();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [jobs, setJobs] = useState([]);
  // IDI-741 D1: the invoices, quotes and jobs reads all answered this load.
  const [primaryReadsOk, setPrimaryReadsOk] = useState(false);
  const [period, setPeriodState] = useState(
    searchParams.get('period') || savedPrefs?.period || 'last_6_months'
  );
  const [compare, setCompare] = useState(false);
  const [hiddenSections, setHiddenSections] = useState(savedPrefs?.hiddenSections || []);
  const [showGearMenu, setShowGearMenu] = useState(false);
  const gearRef = useRef(null);

  // Wrap setPeriod to also update URL
  const setPeriod = (newPeriod) => {
    setPeriodState(newPeriod);
    setSearchParams({ period: newPeriod });
  };

  // Persist preferences
  useEffect(() => {
    savePrefs({ hiddenSections, period });
  }, [hiddenSections, period]);

  // Close gear menu on outside click
  useEffect(() => {
    if (!showGearMenu) return;
    const handler = (e) => {
      if (gearRef.current && !gearRef.current.contains(e.target)) setShowGearMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showGearMenu]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPrimaryReadsOk(false);

    try {
      // PORTICA-56 follow-up: getJobs()/getCustomers() with NO limit fetch the
      // WHOLE tenant dataset in one call -- confirmed live that this times out
      // the Lambda for a real tenant (1,179 jobs). The dashboard is a KPI/
      // activity-feed SUMMARY view, not the canonical Jobs/Customers list (that
      // is JobsPage.js/CustomersPage.js, which already paginate correctly) --
      // capping at 500 avoids the timeout while still covering every
      // realistic dashboard need. A tenant with >500 jobs sees KPIs computed
      // from the most recent 500, not a truncated UI list.
      const [invoiceData, quoteData, jobData, customerData] = await Promise.allSettled([
        invoiceAPI.getInvoices(),
        quoteAPI.getQuotes(),
        jobAPI.getJobs({ limit: 500 }),
        customerAPI.getCustomers({ limit: 500 }),
      ]);

      // Build customer ID → name map
      // Both jobs and customers now return a wrapped {jobs|customers, has_more,
      // next_cursor} shape when limit is passed (PORTICA-56) instead of a
      // bare array -- unwrap it the same way QuotesPage/JobsPage/CustomersPage do.
      const customers = customerData.status === 'fulfilled'
        ? (Array.isArray(customerData.value) ? customerData.value : (customerData.value?.customers || []))
        : [];
      const customerMap = {};
      customers.forEach(c => {
        const id = c.id || c.customerId;
        const name = c.name || c.customerName || c.customer_name;
        if (id && name) customerMap[id] = name;
      });

      const rawInvoices = invoiceData.status === 'fulfilled' ? (Array.isArray(invoiceData.value) ? invoiceData.value : []) : [];
      // IDI-216 (item 3): /v1/quotes now ALWAYS returns the enveloped
      // {quotes,has_more,next_cursor} shape (previously bare-array when no
      // `limit` was passed, which this call site relied on). unwrapList
      // handles both shapes defensively either way.
      const rawQuotes = quoteData.status === 'fulfilled'
        ? unwrapList(quoteData.value, 'quotes', 'DashboardNew').list
        : [];
      const rawJobs = jobData.status === 'fulfilled'
        ? (Array.isArray(jobData.value) ? jobData.value : (jobData.value?.jobs || []))
        : [];

      // Enrich with customer names
      setInvoices(enrichWithCustomerNames(rawInvoices, customerMap));
      setQuotes(enrichWithCustomerNames(rawQuotes, customerMap));
      setJobs(enrichWithCustomerNames(rawJobs, customerMap));
      setPrimaryReadsOk([invoiceData, quoteData, jobData].every(r => r.status === 'fulfilled'));

      if (invoiceData.status === 'rejected' && quoteData.status === 'rejected' && jobData.status === 'rejected') {
        setError('Unable to load dashboard data. Please check your connection and try again.');
      }
    } catch (err) {
      setError('Unable to load dashboard data. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleSection = (section) => {
    setHiddenSections(prev =>
      prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]
    );
  };

  // Compute derived data
  const kpis = computeKPIs(invoices, quotes, jobs, period);
  // Iter-18: revenueTrend/priorRevenueTrend removed — RevenueTrendChart
  // is no longer rendered on Dashboard (still used on Reports Overview).
  const quotesPipeline = computeQuotesPipeline(quotes);
  // Schedule data is now computed inside ScheduleStrip from jobs directly
  const activities = buildActivityFeed(invoices, quotes, jobs);

  const isVisible = (id) => !hiddenSections.includes(id);

  // REQ-WIRE-10: first-run detection. A brand-new tenant has genuinely no data
  // across all three primary datasets — show the getting-started CTA instead of
  // a wall of zeroed KPI cards. Only when the load succeeded (no error).
  // Dev override: /dashboard?view=first-run forces State B for review.
  const devForceFirstRun = searchParams.get('view') === 'first-run';
  const dataEmpty =
    !loading &&
    !error &&
    invoices.length === 0 &&
    quotes.length === 0 &&
    jobs.length === 0;
  const isFirstRun = devForceFirstRun || dataEmpty;
  // IDI-659 D3/D5: only an admin of a truly empty dealer (not the review
  // switch) may be offered setup. IDI-741 D1: a failed invoices, quotes or
  // jobs read means the dealer is unreadable → no card, no flag read.
  const canStartSetup = dataEmpty && primaryReadsOk && isSetupAdmin(authUser);
  // IDI-821 D2: any viewer of a truly empty dealer reads the setup flag once,
  // so a finished dealer hides "Finish setting up" for every role.
  const readSetupFlag = dataEmpty && primaryReadsOk;

  // Iter-18 DASH-1: primary CTA is '+ Create' dropdown with 3 options
  // (New Quote / New Customer / New Event). No 'New Job' — jobs only
  // from Quote conversion (QD-1 wire).
  const createOptions = [
    { label: 'New Quote',    icon: <DocumentTextIcon className="h-4 w-4" />, onClick: () => navigate('/quotes/add') },
    { label: 'New Customer', icon: <UserIcon className="h-4 w-4" />,         onClick: () => navigate('/customers?add=true') },
    { label: 'New Event',    icon: <CalendarDaysIcon className="h-4 w-4" />, onClick: () => navigate('/calendar') },
  ];

  return (
    <div className="px-6">
      {/* Header — no actions in State B (wire: "one red primary: NONE") */}
      <PageHeader
        title="Dashboard"
        actions={!isFirstRun && (
          <DropdownMenu
            ariaLabel="Create menu"
            panelClassName="w-52"
            trigger={(
              <Button variant="primary" aria-label="Create">
                <PlusIcon className="h-4 w-4" aria-hidden="true" />
                Create
                <ChevronDownIcon className="h-4 w-4 ml-1" aria-hidden="true" />
              </Button>
            )}
            items={createOptions}
          />
        )}
      />

      {/* Sub-header row (hidden in first-run State B — no period selector or gear menu) */}
      {!isFirstRun && (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 12,
          marginBottom: 8,
          gap: 12,
        }}
      >
        <PeriodSelector value={period} onChange={setPeriod} compare={compare} onCompareChange={setCompare} />
        <div style={{ position: 'relative' }} ref={gearRef}>
          <button
            onClick={() => setShowGearMenu(!showGearMenu)}
            className="inline-flex items-center justify-center"
            style={{
              width: 36,
              height: 36,
              padding: 0,
              border: '1px solid var(--color-border)',
              background: 'var(--btn-secondary-bg, var(--color-surface))',
              color: 'var(--btn-secondary-text, var(--color-text-default))',
              cursor: 'pointer',
              borderRadius: 'var(--btn-radius)',
            }}
            aria-label="Dashboard settings"
            aria-haspopup="menu"
            aria-expanded={showGearMenu}
          >
            <Cog6ToothIcon style={{ width: 18, height: 18 }} aria-hidden="true" />
          </button>
          {showGearMenu && (
            <div style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              marginTop: 4,
              background: 'var(--color-surface)',
              border: '1px solid var(--neutral-200)',
              borderRadius: 'var(--radius-md)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              zIndex: 50,
              minWidth: 200,
              padding: '8px 0',
            }}>
              <p style={{
                padding: '4px 14px 8px',
                margin: 0,
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: 'var(--color-text-muted)',
              }}>
                Sections
              </p>
              {SECTION_DEFS.map(section => (
                <label
                  key={section.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 14px',
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!hiddenSections.includes(section.id)}
                    onChange={() => toggleSection(section.id)}
                    style={{ accentColor: 'var(--color-action-primary)' }}
                  />
                  <span style={{ color: 'var(--color-text-default)' }}>{section.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {/* First-run State B: no data yet across any dataset (REQ-WIRE-10) */}
      {isFirstRun && <DashboardFirstRun canStartSetup={canStartSetup} readSetupFlag={readSetupFlag} />}

      {/* Error state */}
      {error && (
        <div style={{ marginBottom: 16 }}>
          <Alert variant="error" onClose={() => setError(null)}>
            {error}
            <button
              onClick={fetchData}
              style={{
                marginLeft: 12,
                padding: '4px 10px',
                border: '1px solid currentColor',
                borderRadius: 4,
                background: 'transparent',
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              Retry
            </button>
          </Alert>
        </div>
      )}

      {/* Dashboard content (hidden on first-run) */}
      {!isFirstRun && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* KPI Row */}
        {isVisible('kpis') && (
          <ChartErrorBoundary fallbackMessage="Unable to load KPI cards">
            <KPIRow kpis={kpis} loading={loading} />
          </ChartErrorBoundary>
        )}

        {/* Region 2: Revenue (2/3) + Schedule (1/3) */}
        {/* Iter-18: Region 2 — Quotes Pipeline (2/3) + Schedule Strip (1/3).
            RevenueTrend removed from Dashboard per operator feedback; still
            available on Reports Overview. */}
        {(isVisible('charts') || isVisible('schedule')) && (
          <div className="dashboard-region-row" style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
            gap: 16,
          }}>
            {isVisible('charts') && (
              <ChartErrorBoundary fallbackMessage="Unable to load pipeline chart">
                <QuotesPipelineDonut data={quotesPipeline} loading={loading} />
              </ChartErrorBoundary>
            )}
            {isVisible('schedule') && (
              <ChartErrorBoundary fallbackMessage="Unable to load schedule">
                <ScheduleStrip />
              </ChartErrorBoundary>
            )}
          </div>
        )}

        {/* Jobs Table (Region 3) */}
        {isVisible('jobs_table') && (
          <ChartErrorBoundary fallbackMessage="Unable to load jobs table">
            <DashboardJobsTable jobs={jobs} loading={loading} onRefresh={fetchData} />
          </ChartErrorBoundary>
        )}

        {/* Iter-18: Region 4 — Recent Activity below Jobs (per operator Round 4). */}
        {isVisible('activity') && (
          <ChartErrorBoundary fallbackMessage="Unable to load activity feed">
            <ActivityFeed activities={activities} loading={loading} />
          </ChartErrorBoundary>
        )}
      </div>
      )}

      {/* Shimmer animation keyframes + responsive */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @media (max-width: 768px) {
          .dashboard-region-row {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
